import { useEffect, useState } from "react";
import { FileUp, ScanSearch, CheckCircle2, AlertTriangle, Lightbulb, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const readFile = (file) => new Promise((resolve) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.readAsDataURL(file); });
const scoreCls = (s) => (s >= 75 ? "text-emerald-600" : s >= 50 ? "text-amber-600" : "text-red-600");

const List = ({ title, items, icon: Icon, cls, testId }) => !items?.length ? null : (
  <div data-testid={testId}>
    <div className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-1.5"><Icon size={15} className={cls} />{title}</div>
    <ul className="space-y-1.5">{items.map((x, i) => <li key={i} className="text-sm text-slate-600 leading-relaxed pr-4 relative before:content-[''] before:absolute before:right-0 before:top-2 before:w-1.5 before:h-1.5 before:rounded-full before:bg-slate-300">{x}</li>)}</ul>
  </div>
);

const Chips = ({ title, items, cls, testId }) => !items?.length ? null : (
  <div data-testid={testId}>
    <div className="text-xs font-semibold text-slate-500 mb-1.5">{title}</div>
    <div className="flex flex-wrap gap-1.5">{items.map((k, i) => <span key={i} className={`text-xs font-medium rounded-md px-2 py-1 border ${cls}`}>{k}</span>)}</div>
  </div>
);

const Result = ({ r }) => (
  <div className="space-y-5" data-testid="ats-result">
    <div className="flex items-center gap-4">
      <div className={`text-4xl font-bold font-mono tabular-nums ${scoreCls(r.score)}`} data-testid="ats-score">{r.score}<span className="text-base text-slate-400">/100</span></div>
      <p className="text-sm text-slate-600 leading-relaxed" data-testid="ats-summary">{r.summary}</p>
    </div>
    <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full ${r.score >= 75 ? "bg-emerald-500" : r.score >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${r.score}%` }} /></div>
    <Chips title="كلمات مفتاحية مطابقة" items={r.matched_keywords} cls="bg-emerald-50 border-emerald-200 text-emerald-700" testId="ats-matched" />
    <Chips title="كلمات مفتاحية ناقصة" items={r.missing_keywords} cls="bg-red-50 border-red-200 text-red-700" testId="ats-missing" />
    <List title="توصيات التحسين" items={r.recommendations} icon={Lightbulb} cls="text-sun" testId="ats-recommendations" />
    <List title="نقاط القوة" items={r.strengths} icon={CheckCircle2} cls="text-emerald-600" testId="ats-strengths" />
    <List title="مشاكل التنسيق" items={r.format_issues} icon={AlertTriangle} cls="text-amber-600" testId="ats-format" />
  </div>
);

export const CvAnalyzer = ({ l }) => {
  const { user, login } = useAuth();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (user) api.get(`/listings/${l.id}/cv-analysis`).then((r) => r.data?.result && setRes(r.data.result)).catch(() => {});
  }, [user, l.id]);

  const run = async () => {
    if (!file) return toast.error("اختر ملف السيرة الذاتية أولاً");
    if (file.size > 5_000_000) return toast.error("الحد الأقصى للملف 5 ميجا");
    setBusy(true);
    try { const { data } = await api.post(`/listings/${l.id}/cv-analysis`, { document: await readFile(file) }); setRes(data.result); toast.success("تم تحليل السيرة"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5" data-testid="cv-analyzer">
      <div>
        <h2 className="font-bold flex items-center gap-2"><ScanSearch size={18} className="text-slate-600" />فحص السيرة الذاتية (ATS)</h2>
        <p className="text-sm text-slate-500 mt-1">ارفع سيرتك لنقيس توافقها مع هذه الوظيفة ونعطيك توصيات للتحسين. تُرفق السيرة مع طلب تقديمك للإدارة.</p>
      </div>
      {!user ? <Button data-testid="cv-login-btn" onClick={login} className="rounded-lg bg-brand hover:bg-brand-dark">سجّل الدخول لفحص سيرتك</Button> : (
        <div className="flex flex-col sm:flex-row gap-3">
          <label className="flex-1 flex items-center gap-3 border-2 border-dashed border-slate-200 rounded-lg p-3 cursor-pointer hover:border-slate-400 transition-colors">
            <FileUp size={18} className="text-slate-500 shrink-0" />
            <span className="text-sm text-slate-600 truncate">{file ? file.name : "ملف PDF أو Word (docx) — حتى 5 ميجا"}</span>
            <input data-testid="cv-file-input" type="file" accept="application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
          </label>
          <Button data-testid="cv-analyze-btn" disabled={busy} onClick={run} className="rounded-lg bg-sun hover:bg-sun-dark h-auto py-3 px-6">{busy ? "جارِ التحليل... (قد يستغرق 30 ثانية)" : "حلّل سيرتي"}</Button>
        </div>
      )}
      {res && <Result r={res} />}
      {!res && user && !busy && <div className="text-xs text-slate-400 flex items-center gap-1"><XCircle size={12} />لم تُحلَّل سيرة لهذه الوظيفة بعد</div>}
    </div>
  );
};
